import axios from 'axios';
import jwtDecode from 'jwt-decode';
import FuseUtils from '@fuse/FuseUtils';
import qs from 'qs';
import * as Actions from 'app/store/actions';

class jwtService extends FuseUtils.EventEmitter {

    init() {
        this.setInterceptors();
        this.handleAuthentication();
    }

    setInterceptors = () => {
        axios.interceptors.response.use(response => {
            return response;
        }, err => {
            return new Promise((resolve, reject) => {
                if (err.response !== undefined) {
                    if (err.response.status === 401 && err.config && !err.config.__isRetryRequest) {
                        // if you ever get an unauthorized response, logout the user
                        this.emit('onAutoLogout', 'Invalid access_token');
                        this.setSession(null);
                    }
                }
                throw err;
            });
        });
    };

    handleAuthentication = () => {

        let access_token = this.getAccessToken();

        if (!access_token) {
            return;
        }

        if (this.isAuthTokenValid(access_token)) {
            this.setSession(access_token);
            this.emit('onAutoLogin', true);
        } else {
            this.setSession(null);
            this.emit('onAutoLogout', 'El acesso ha caducado.');
        }
    };

    // Token temporal que devuelve /users/login (o /users/login-azure) al validar la
    // identidad; /users/login-empresa lo exige para emitir el token de la empresa elegida.
    preAuthToken = null;

    // Paso 2 del login web: ya se validó usuario/contraseña (signInWithEmailAndPassword)
    // y el usuario eligió una empresa de la lista devuelta ahí. El backend
    // resuelve el perfil que tiene EN esa empresa y emite el token. Si tiene
    // más de un perfil en esa empresa, el backend no emite token todavía:
    // devuelve requiresProfile=true + la lista, y hay que llamar de nuevo
    // pasando "perfil" una vez que el usuario elige.
    // `email` se mantiene por compatibilidad con los llamadores: el backend toma el
    // usuario del token temporal del paso 1.
    signInWithCompany = (email, companyId, perfil) => {
        var data = { 'preauth_token': this.preAuthToken, 'company_id': companyId };
        if (perfil) {
            data.perfil = perfil;
        }
        const options = {
            method: 'POST',
            url: process.env.REACT_APP_API_HOST + '/users/login-empresa',
            headers: { 'content-type': 'application/x-www-form-urlencoded' },
            data: qs.stringify(data)
        };
        return new Promise((resolve, reject) => {
            axios(options).then(response => {
                if (response.data.requiresProfile) {
                    resolve({
                        requiresProfile: true,
                        perfiles: response.data.perfiles,
                        email: response.data.email,
                        companyId: response.data.company_id
                    });
                } else if (response.data.auth) {
                    this.preAuthToken = null;
                    this.setSession(response.data.token);
                    resolve({ requiresProfile: false, user: response.data.user });
                } else {
                    reject(response.data.error || response.data.message);
                }
            }).catch((error) => {
                reject(error.response?.data?.message || 'No se pudo ingresar a la empresa seleccionada');
            });
        });
    };

    // Cambio de empresa activa sin re-loguearse: usa el token vigente para
    // pedirle al backend un nuevo token/perfil scopeado a la nueva empresa.
    // Misma lógica de requiresProfile que signInWithCompany.
    switchCompany = (companyId, perfil) => {
        const options = {
            method: 'POST',
            url: process.env.REACT_APP_API_HOST + '/users/switch-empresa',
            headers: {
                'content-type': 'application/json',
                'Authorization': `Bearer ${this.getAccessToken()}`
            },
            data: { company_id: companyId, perfil }
        };
        return new Promise((resolve, reject) => {
            axios(options).then(response => {
                if (response.data.requiresProfile) {
                    resolve({
                        requiresProfile: true,
                        perfiles: response.data.perfiles,
                        companyId: response.data.company_id
                    });
                } else if (response.data.auth) {
                    this.setSession(response.data.token);
                    resolve({ requiresProfile: false, user: response.data.user });
                } else {
                    reject(response.data.error || response.data.message);
                }
            }).catch((error) => {
                reject(error.response?.data?.message || 'No se pudo cambiar de empresa');
            });
        });
    };

    signInWithEmailAndPassword = (email, password) => {
        var data = { 'email': email.toLowerCase().trim(), 'password': password.trim() };
        //var dataEncrypt = qs.stringify(data);
        const options = {
            method: 'POST',
            url: process.env.REACT_APP_API_HOST + '/users/login',
            headers: { 'content-type': 'application/x-www-form-urlencoded' },
            data: qs.stringify(data)
        };
        return new Promise((resolve, reject) => {
            axios(options).then(response => {
                if (response.data.status == 'success') {
                    this.preAuthToken = response.data.preauth_token;
                    resolve(response);
                } else {
                    this.emit('onAutoLogout', 'Usuario o contraseña erroneos');
                }
            }).catch(() => {
                this.emit('onAutoLogout', 'Usuario o contraseña erroneos');
            });
        });
    };

    // Login con Microsoft: el backend valida el idToken de MSAL y devuelve las empresas
    // del usuario junto con el token temporal para /users/login-empresa.
    signInWithAzure = idToken => {
        return axios.post(process.env.REACT_APP_API_HOST + '/users/login-azure', { idToken })
            .then(response => {
                if (response.data.status == 'success') {
                    this.preAuthToken = response.data.preauth_token;
                }
                return response;
            });
    };

    signInWithToken = () => {
        const options = {
            method: 'GET',
            url: process.env.REACT_APP_API_HOST + '/users/access-token',
            headers: {
                'content-type': 'application/x-access-token',
                "Authorization": `Bearer ${this.getAccessToken()}`
            } //,
            //data: qs.stringify(data)
        };
        return new Promise((resolve, reject) => {
            axios(options).then(response => {
                if (response.data.user) {
                    this.setSession(response.data.token);
                    resolve(response.data.user);
                } else {
                    reject(response.data.error);
                }
            }).catch(error => {
                Actions.showMessage({ message: error.message })
            });
        });
    };

    exchangeGoogleCode = (code, state) => {
        return axios.post(
            process.env.REACT_APP_API_HOST + '/users/google/exchange-code',
            { code, state }
        ).then((response) => {
            if (response.data.status === 'success') {
                return response.data;
            }

            throw new Error(response.data.message || 'No se pudo validar el código de Google');
        });
    };

    // Igual que signInWithCompany/switchCompany: si el usuario tiene más de
    // un perfil en la empresa elegida, el backend no loguea todavía y
    // devuelve requiresProfile=true + la lista para elegir.
    finalizeGoogleLogin = (preauthToken, companyId, perfil) => {
        return new Promise((resolve, reject) => {
            axios.post(
                process.env.REACT_APP_API_HOST + '/users/google/finalize',
                {
                    preauth_token: preauthToken,
                    company_id: companyId,
                    perfil
                }
            ).then((response) => {
                if (response.data.requiresProfile) {
                    resolve({
                        requiresProfile: true,
                        perfiles: response.data.perfiles,
                        companyId: response.data.company_id
                    });
                } else if (response.data.auth) {
                    this.setSession(response.data.token);
                    resolve({ requiresProfile: false, user: response.data.user });
                } else {
                    reject(response.data.error || response.data.message);
                }
            }).catch((error) => {
                reject(error.response?.data?.message || 'No se pudo finalizar el login con Google');
            });
        });
    };

    setSession = access_token => {
        if (access_token) {
            localStorage.setItem('jwt_access_token', access_token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        } else {
            localStorage.removeItem('jwt_access_token');
            delete axios.defaults.headers.common['Authorization'];
        }
    };

    logout = () => {
        this.setSession(null);
        //window.location.reload(true)
    };

    isAuthTokenValid = access_token => {
        if (!access_token) {
            return false;
        }
        const decoded = jwtDecode(access_token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
            return false;
        } else {
            return true;
        }
    };

    getAccessToken = () => {
        return window.localStorage.getItem('jwt_access_token');
    };

    // Las subidas con XMLHttpRequest no usan axios: se agrega el token a mano
    // (llamar después de xhr.open()).
    applyAuthHeader = xhr => {
        const token = this.getAccessToken();
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    };
}

const instance = new jwtService();

export default instance;
